import { useCallback, useEffect, useRef, useState } from "react";

function containsRelatedTarget(event: FocusEvent) {
  if (
    event.currentTarget instanceof HTMLElement &&
    event.relatedTarget instanceof HTMLElement
  ) {
    return event.currentTarget.contains(event.relatedTarget);
  }

  return false;
}

export interface UseFocusWithinOptions {
  onFocus?: (event: FocusEvent) => void;
  onBlur?: (event: FocusEvent) => void;
}

export interface UseFocusWithinReturnValue<T extends HTMLElement = any> {
  ref: React.RefObject<T>;
  focused: boolean;
}

/**
 * Custom hook for tracking focus state within a DOM element.
 *
 * Features:
 * - Tracks whether any child element has focus
 * - Provides a ref object to attach to the target element
 * - Handles focus events with proper cleanup
 * - Supports custom onFocus and onBlur callbacks
 * - Prevents false blur events when focus moves between children
 * - Automatically cleans up event listeners on unmount
 *
 * @param options Configuration options for the hook
 * @param options.onFocus Optional callback when focus enters the element
 * @param options.onBlur Optional callback when focus leaves the element
 * @returns Object containing ref and focused state
 *
 * @example
 * const { ref, focused } = useFocusWithin({
 *   onFocus: () => console.log('Focus entered'),
 *   onBlur: () => console.log('Focus left')
 * });
 *
 * return <div ref={ref} className={focused ? 'focused' : ''}>Content</div>;
 */
export function useFocusWithin<T extends HTMLElement = any>({
  onBlur,
  onFocus,
}: UseFocusWithinOptions = {}): UseFocusWithinReturnValue<T> {
  const [focused, setFocused] = useState(false);
  const focusedRef = useRef(false);
  const elementRef = useRef<T>(null);

  const _setFocused = useCallback((value: boolean) => {
    setFocused(value);
    focusedRef.current = value;
  }, []);

  const handleFocusIn = useCallback(
    (event: FocusEvent) => {
      if (!focusedRef.current) {
        _setFocused(true);
        onFocus?.(event);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onFocus],
  );

  const handleFocusOut = useCallback(
    (event: FocusEvent) => {
      if (focusedRef.current && !containsRelatedTarget(event)) {
        _setFocused(false);
        onBlur?.(event);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onBlur],
  );

  useEffect(() => {
    const element = elementRef.current;
    if (!element) {
      return;
    }

    element.addEventListener("focusin", handleFocusIn);
    element.addEventListener("focusout", handleFocusOut);

    return () => {
      element.removeEventListener("focusin", handleFocusIn);
      element.removeEventListener("focusout", handleFocusOut);
    };
  }, [handleFocusIn, handleFocusOut]);

  return { ref: elementRef, focused };
}
